// src/auth/turnstile-client.ts
//
// V3 §5.7 + Pay-to-Play §3.1 — lazy Cloudflare Turnstile loader. Exposes
// `getTurnstileToken` which returns a discriminated `TurnstileTokenResult`
// so the caller (e.g. `runSignupChallenge` in src/auth/anonymous.ts) can
// distinguish "CF scored me as a bot" (→ interactive retry) from "CF was
// unreachable / widget threw" (→ outage signal → server fails open to PoW).
//
// Return shape:
//   - { kind: 'token',  token }      — real CF-issued token.
//   - { kind: 'stub'  }              — no VITE_TURNSTILE_SITE_KEY or script
//                                      failed to load (dev / ad-block).
//                                      Server accepts `ts-dev-stub` ONLY when
//                                      TURNSTILE_SECRET_KEY is unset.
//   - { kind: 'outage', reason }     — widget threw / load timeout / expired.
//                                      Server short-circuits to fail-open PoW.
//   - { kind: 'rejected', errorCodes } — CF returned error-codes (bot-scored).
//                                        Caller should retry interactively.
//
// NOTE: the literal `'ts-dev-stub'` / `'ts-outage-signal'` strings must live
// ONLY in this file on the client side — a CI guard (see §3.7 in the
// handoff) greps for leaks into other client files.
//
// Legacy callers that just want a string can use `getTurnstileTokenOrStub()`.

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const STUB = 'ts-dev-stub';
const OUTAGE = 'ts-outage-signal';
const LOAD_TIMEOUT_MS = 4_000;

export type TurnstileTokenResult =
  | { kind: 'token'; token: string }
  | { kind: 'stub' }
  | { kind: 'outage'; reason: 'load_timeout' | 'expired' | 'widget_throw' }
  | { kind: 'rejected'; errorCodes: string[] };

/** Dev/outage sentinels the server recognizes. Exported for test assertions. */
export const TURNSTILE_STUB_TOKEN = STUB;
export const TURNSTILE_OUTAGE_TOKEN = OUTAGE;

interface Turnstile {
  render: (container: string | HTMLElement, opts: {
    sitekey: string;
    callback?: (token: string) => void;
    'error-callback'?: (errorCode?: string) => void;
    'expired-callback'?: () => void;
    'timeout-callback'?: () => void;
    appearance?: 'always' | 'execute' | 'interaction-only';
    size?: 'normal' | 'flexible' | 'compact' | 'invisible';
    theme?: 'light' | 'dark' | 'auto';
  }) => string;
  execute?: (widgetId: string) => void;
  remove?: (widgetId: string) => void;
  reset?: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

let scriptPromise: Promise<Turnstile | null> | null = null;

function loadScript(): Promise<Turnstile | null> {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve) => {
      if (typeof window === 'undefined') { resolve(null); return; }
      if (window.turnstile) { resolve(window.turnstile); return; }
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      const timer = setTimeout(() => resolve(null), LOAD_TIMEOUT_MS);
      script.onload = () => {
        clearTimeout(timer);
        resolve(window.turnstile ?? null);
      };
      script.onerror = () => {
        clearTimeout(timer);
        resolve(null);
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * Request a Turnstile token. See `TurnstileTokenResult` for the full
 * discrimination table. `mode='invisible'` scores silently; `'interactive'`
 * renders a visible widget (used as an interactive retry after a bot-score
 * rejection).
 */
export async function getTurnstileToken(
  _action: string,
  mode: 'invisible' | 'interactive' = 'invisible',
): Promise<TurnstileTokenResult> {
  if (!SITE_KEY) return { kind: 'stub' };
  const ts = await loadScript();
  if (!ts) return { kind: 'stub' };

  // Create a detached container so render() doesn't require an on-page slot.
  const container = document.createElement('div');
  container.style.display = mode === 'invisible' ? 'none' : 'block';
  container.setAttribute('data-testid', 'turnstile-widget');
  document.body.appendChild(container);

  return new Promise<TurnstileTokenResult>((resolve) => {
    let settled = false;
    let widgetId: string | null = null;
    const cleanup = () => {
      try { if (widgetId && ts.remove) ts.remove(widgetId); } catch { /* no-op */ }
      try { container.remove(); } catch { /* no-op */ }
    };
    const done = (r: TurnstileTokenResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(r);
    };

    try {
      // CF's error-callback signature is best-effort: it can fire with 0..N
      // error codes. We collect them into an array. Empty-array → widget
      // threw (outage-like); non-empty → CF scored the user (rejected).
      const errorCodes: string[] = [];
      widgetId = ts.render(container, {
        sitekey: SITE_KEY,
        size: mode === 'invisible' ? 'invisible' : 'flexible',
        appearance: mode === 'invisible' ? 'execute' : 'always',
        callback: (t) => done({ kind: 'token', token: t }),
        'error-callback': (code) => {
          if (typeof code === 'string' && code.length > 0) {
            errorCodes.push(code);
            done({ kind: 'rejected', errorCodes: [...errorCodes] });
          } else {
            done({ kind: 'outage', reason: 'widget_throw' });
          }
        },
        'expired-callback': () => done({ kind: 'outage', reason: 'expired' }),
        'timeout-callback': () => done({ kind: 'outage', reason: 'load_timeout' }),
      });
      if (mode === 'invisible' && ts.execute && widgetId) ts.execute(widgetId);
    } catch {
      done({ kind: 'outage', reason: 'widget_throw' });
    }

    setTimeout(() => done({ kind: 'outage', reason: 'load_timeout' }), LOAD_TIMEOUT_MS);
  });
}

/**
 * Legacy string-returning wrapper. Kept so callers that only consume a
 * string token (and accept `'ts-dev-stub'` as the dev fallback) don't need
 * to update. Maps:
 *   { kind: 'token' }    → token
 *   { kind: 'stub' }     → 'ts-dev-stub'
 *   { kind: 'outage' }   → 'ts-outage-signal'
 *   { kind: 'rejected' } → '' (empty string; server will 400)
 *
 * NEW callers should prefer `getTurnstileToken` directly so they can branch
 * on outage vs rejected (the outage branch triggers the PoW fail-open path,
 * the rejected branch triggers interactive retry).
 */
export async function getTurnstileTokenOrStub(
  action: string,
  mode: 'invisible' | 'interactive' = 'invisible',
): Promise<string> {
  const r = await getTurnstileToken(action, mode);
  switch (r.kind) {
    case 'token':    return r.token;
    case 'stub':     return STUB;
    case 'outage':   return OUTAGE;
    case 'rejected': return '';
  }
}
